import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { useListOrdersQuery } from '../../app/config/redux/slices/api/orderApiSlice';

const OrderList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [limit] = useState(10);
    
    // Get current page from URL or default to 1
    const page = parseInt(searchParams.get('page')) || 1;

    const { userInfo } = useSelector((state) => state.auth);
    
    const { data, error, isLoading } = useListOrdersQuery({
        limit,
        page,
        token: userInfo?.access_token
    });

    // Accessing the nested data structure from your asset
    const orderData = data?.data; 
    const orders = orderData?.data; // The array of orders
    const totalPages = orderData?.last_page || 1;

    const updatePageInUrl = (newPage) => {
        const params = new URLSearchParams(searchParams);
        params.set("page", newPage);
        setSearchParams(params);
        // Scroll to top when page changes
        window.scrollTo(0, 0);
    };

    const handleNextPage = (e) => {
        e.preventDefault();
        if (orderData?.next_page_url) {
            updatePageInUrl(page + 1);
        }
    };

    const handlePrevPage = (e) => {
        e.preventDefault();
        if (orderData?.prev_page_url) {
            updatePageInUrl(page > 1 ? page - 1 : page);
        }
    };

    const handlePageChange = (e, newPage) => {
        e.preventDefault();
        updatePageInUrl(newPage);
    };

    if (isLoading) return (
        <div className="d-flex justify-content-center py-80">
            <div className="spinner-border text-main-600" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
    
    if (error) return <div className="container py-80 text-center text-danger">Error loading orders. Please try again later.</div>;
    if (!orders || orders.length === 0) return <div className="container py-80 text-center">No orders found.</div>;

    return (
        <section className="shop py-30 bg-light-section">
            <div className="container container-lg">
                <div className="row row-cols-1 row-cols-md-2 g-4">
                    {orders.map((order) => {
                        const statusColor = order?.order_status?.colour || '#28a745';
                        return (
                            <div key={order.id} className="col">
                                <Link 
                                    to={`/order-details/${order.id}`} 
                                    className="order-card-link text-decoration-none d-block h-100"
                                >
                                    <div className="bg-white rounded-16 border border-gray-100 shadow-sm p-16 p-sm-20 position-relative overflow-hidden transition-all hover-shadow-md h-100 d-flex flex-column justify-content-between">
                                        <div 
                                            className="position-absolute top-0 start-0 h-100" 
                                            style={{ width: '6px', backgroundColor: statusColor }}
                                        ></div>

                                        <div className="d-flex align-items-start justify-content-between gap-12 mb-16">
                                            <div className="d-flex align-items-center gap-12">
                                                <div 
                                                    className="p-10 rounded-12 d-flex align-items-center justify-content-center" 
                                                    style={{ backgroundColor: `${statusColor}15`, color: statusColor }}
                                                >
                                                    <i className="ph-fill ph-package text-xl"></i>
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 text-md fw-bold text-heading">Order #{order.id}</h6>
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(order.created_at).toLocaleDateString('en-IN', {
                                                            day: 'numeric', month: 'short', year: 'numeric'
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <span 
                                                className="px-10 py-4 rounded-pill text-xs fw-bold border text-nowrap" 
                                                style={{ 
                                                    backgroundColor: `${statusColor}10`, 
                                                    color: statusColor, 
                                                    borderColor: `${statusColor}30` 
                                                }}
                                            >
                                                {order?.order_status?.name}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center justify-content-between mt-auto pt-12 border-top border-gray-50">
                                            <div>
                                                <span className="d-block text-xs text-gray-400 fw-medium">Total Amount</span>
                                                <span className="text-md fw-bold text-main-600">₹{order.price}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-4 text-main-600 text-sm fw-semibold">
                                                Details <i className="ph ph-arrow-right"></i>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>

                {/* Pagination Start */}
                {totalPages > 1 && (
                    <div className="mt-40">
                        <ul className="pagination d-flex justify-content-center flex-wrap gap-16">
                            {/* Previous Button */}
                            <li className={`page-item ${!orderData?.prev_page_url ? "disabled" : ""}`}>
                                <Link
                                    className="page-link h-48 w-48 d-flex align-items-center justify-content-center rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                    onClick={handlePrevPage}
                                    to="#"
                                >
                                    <i className="ph-bold ph-arrow-left" />
                                </Link>
                            </li>

                            {/* Page Numbers */}
                            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNum) => (
                                <li key={pageNum} className={`page-item ${pageNum === page ? "active" : ""}`}>
                                    <Link
                                        className={`page-link h-48 w-48 d-flex align-items-center justify-content-center rounded-8 fw-medium border border-gray-100 ${
                                            pageNum === page ? "bg-main-600 text-white" : "text-neutral-600"
                                        }`}
                                        onClick={(e) => handlePageChange(e, pageNum)}
                                        to="#"
                                    >
                                        {pageNum}
                                    </Link>
                                </li>
                            ))}

                            {/* Next Button */}
                            <li className={`page-item ${!orderData?.next_page_url ? "disabled" : ""}`}>
                                <Link
                                    className="page-link h-48 w-48 d-flex align-items-center justify-content-center rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                    onClick={handleNextPage}
                                    to="#"
                                >
                                    <i className="ph-bold ph-arrow-right" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                )}
                {/* Pagination End */}
            </div>
        </section>
    );
};

export default OrderList;